var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useState, useCallback } from 'react';
import { apiCanCreateProfessionalRfpMember } from '@/actions/rfpProMemberActions';
import { notify, wrapNotifyError } from '@/helpers/action';
import { isAxiosError } from '@/types/api/apiErrorTypes';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { BAD_RFP_OFFER_MEMBER_ADMIN_NOT_HAS_FORCE_INVITE } from '@/utils/constants/api/responseCode';
import { RFP_ADD_MEMBER_ERROR_MESSAGES } from '@/utils/constants/rfp/rfpErrorMessage';
/**
 * 案件メンバー追加用 hooks
 * @param options
 * @returns
 */
export const useRfpMemberAddModal = (options) => {
    const { dispatch, id, onSubmit, resultMessage, unmountRef } = options;
    /** メンバー追加モーダル 表示状態 */
    const [isShow, changeModal] = useState(false);
    /** 権限がないかどうか */
    const [isMissingRole, changeIsMissingRole] = useState(false);
    /** 強制招待のフラグ付与対象のメールアドレス */
    const [forceInvites, changeForceInvites] = useState([]);
    /**
     * 権限, 強制招待のフラグ state 初期化
     */
    const clearIsMissingRole = useCallback(() => {
        changeIsMissingRole(false);
        changeForceInvites([]);
    }, [changeIsMissingRole, changeForceInvites]);
    /**
     * メンバー追加モーダル表示
     */
    const showModal = useCallback(() => {
        changeForceInvites([]);
        changeModal(true);
    }, [changeForceInvites, changeModal]);
    /**
     * メンバー追加モーダル非表示
     */
    const hideModal = useCallback(() => {
        changeModal(false);
        clearIsMissingRole();
    }, [changeModal, clearIsMissingRole]);
    /**
     * メンバー追加処理
     */
    const wrapOnSubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        const { email } = values;
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const apiValues = Object.assign(Object.assign({}, values), { forceInvite: !!forceInvites.includes(email) });
                changeIsMissingRole(false);
                changeForceInvites((prevForceInvites) => [
                    ...prevForceInvites.filter((prevForceInvite) => prevForceInvite !== email),
                ]);
                const action = apiCanCreateProfessionalRfpMember({
                    json: apiValues,
                    path: { id },
                }, {});
                yield wrapDispatch(dispatch, action);
                yield onSubmit(apiValues);
                if (!unmountRef.current) {
                    hideModal();
                    if (resultMessage) {
                        notify(resultMessage);
                    }
                }
            }
            catch (error) {
                if (unmountRef.current) {
                    return;
                }
                if (isAxiosError(error)) {
                    const errorCode = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.code;
                    // 責任者を招待しようとしたが強制招待のフラグがない場合
                    if (errorCode === BAD_RFP_OFFER_MEMBER_ADMIN_NOT_HAS_FORCE_INVITE) {
                        changeIsMissingRole(true);
                        changeForceInvites((prevForceInvites) => [
                            ...prevForceInvites.filter((prevForceInvite) => prevForceInvite !== email),
                            email,
                        ]);
                        return;
                    }
                    const errorMessage = RFP_ADD_MEMBER_ERROR_MESSAGES[errorCode] || '';
                    if (errorMessage) {
                        /* eslint-disable react/react-in-jsx-scope, no-throw-literal */
                        throw _jsx("span", { children: errorMessage });
                    }
                }
                throw error;
            }
        }));
    }), [
        id,
        hideModal,
        dispatch,
        unmountRef,
        onSubmit,
        resultMessage,
        changeIsMissingRole,
        forceInvites,
        changeForceInvites,
    ]);
    return {
        clearIsMissingRole,
        hideModal,
        isMissingRole,
        isShow,
        onSubmit: wrapOnSubmit,
        showModal,
    };
};
